import React from "react";

import { formatCurrency } from "../../../pages/admin/uebersicht/uebersichtHelpers";

/**
 * The five figures that were already on this panel, on the contracts they always read.
 *
 * THIS BLOCK IS DELIBERATELY SEPARATE FROM EVERYTHING ABOVE IT
 * -----------------------------------------------------------
 * Not decoration: the figures here come from `/dashboard/kpis` and from a
 * client-side sum over the unit list, NOT from the Performance 2.0 contract. Two
 * of them use the legacy cost basis — the raw total that folds unrecognised cost
 * rows in, which is exactly what R1 replaced. Printed among the four cards at the
 * top, "Ø Gewinn/Zi." would read as a per-room breakdown of "Erwartetes
 * Objektergebnis", and it is not: different costs, different population,
 * different word. The heading and the rule above it are what keep the two apart.
 *
 * NOTHING IS RECOMPUTED AND NOTHING IS RENAMED. Every expression below is the one
 * the page carried inline, moved verbatim — same source path, same null guard,
 * same formatter, same German label. (P2 changed exactly one SOURCE, and no
 * formula: "Leerstand Tage" now reads the Performance 2.0 period instead of the
 * legacy payload. See the P2 note below.) "Ø Gewinn/Zi." keeps the word "Gewinn"
 * precisely because renaming it would suggest its meaning had changed with it;
 * the structural guard in `performanceGuards.test.js` knows this one label as its
 * single allowed exception and forbids the vocabulary everywhere else on the tab.
 *
 * PORTFOLIO-POTENZIAL has no loading guard here, exactly as before: it is summed
 * from `units`/`rooms` the page already holds, so it is CHF 0 until they land
 * rather than a dash. That is pre-existing behaviour and is not changed here.
 *
 * P2 — THE MIXED-PERIOD BUG THIS BLOCK CARRIED
 * --------------------------------------------
 * Under a multi-month filter these five answered for FOUR different windows at
 * once, unlabelled and visually equal: "Prognose" for a following month, two
 * averages for the to-month, "Leerstand Tage" for the whole range (the legacy
 * payload aggregates that one), and "Portfolio-Potenzial" for today. A reader
 * comparing them was comparing periods, not figures.
 *
 * Each is now classified rather than relabelled:
 *
 *   Prognose, Ø Umsatz/Zi., Ø Gewinn/Zi. — legacy, to-month only. Performance 2.0
 *     publishes no period equivalent, so under a range they are HIDDEN. Showing
 *     them beneath a period heading would assert a window they do not cover, and
 *     inventing a period forecast here would be a KPI this frontend is not
 *     allowed to define.
 *   Leerstand Tage — now read from the PERFORMANCE 2.0 period
 *     (`selected.vacant_room_days`), not from the legacy payload. The legacy
 *     figure happens to be range-aware, but it is the legacy room-day truth, and
 *     P1 proved the two families are not interchangeable.
 *   Portfolio-Potenzial — a current-state figure and now says so, so it cannot
 *     read as a period aggregate.
 */
export default function PerformanceSecondaryKpis({
  legacyKpis,
  legacyKpisLoading = false,
  portfolioPotential = 0,
  portfolioLoading = false,
  selected = null,
  isRange = false,
  loading = false,
}) {
  const cards = legacyKpis?.summary_cards;
  const ready = Boolean(legacyKpis) && !legacyKpisLoading;

  // Legacy, to-month only. Under a range they are dropped from the list rather
  // than rendered as an em-dash: a dash says "we asked and got nothing", which
  // is not what happened — the figure exists, it just does not answer for this
  // window.
  const legacyMonthFigures = isRange
    ? []
    : [
        {
          key: "forecast",
          label: "Prognose",
          value:
            ready && cards?.forecast_next_month?.revenue != null
              ? formatCurrency(cards.forecast_next_month.revenue)
              : "—",
        },
        {
          key: "revenue-per-room",
          label: "Ø Umsatz/Zi.",
          value:
            ready && cards?.average_revenue_per_room?.value != null
              ? formatCurrency(cards.average_revenue_per_room.value)
              : "—",
        },
        {
          key: "profit-per-unit",
          label: "Ø Gewinn/Zi.",
          value:
            ready && cards?.average_profit_per_unit?.value != null
              ? formatCurrency(cards.average_profit_per_unit.value)
              : "—",
        },
      ];

  const figures = [
    ...legacyMonthFigures,
    {
      key: "vacant-days",
      label: "Leerstand Tage",
      // Performance 2.0's own room-day truth for the answered period — the same
      // family the Auslastung card above divides, so the two can no longer
      // disagree about which months they cover.
      value:
        !loading && selected?.vacant_room_days != null
          ? selected.vacant_room_days
          : "—",
    },
    {
      key: "portfolio-potential",
      label: "Portfolio-Potenzial",
      value: formatCurrency(portfolioPotential),
      hint: "Aktueller Stand",
    },
  ];


  return (
    <section
      data-testid="performance-secondary-kpis"
      aria-labelledby="performance-secondary-heading"
      className="rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <h2
        id="performance-secondary-heading"
        className="m-0 text-[13px] font-semibold text-card-foreground"
      >
        Weitere Kennzahlen
      </h2>
      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
        Aus den bestehenden Portfolio-KPIs — nicht Teil des Objektergebnisses oben.
      </p>

      {/* The page's mount bundle has not resolved yet. Kept from the block this
          replaced, where the same gate hid the figures behind exactly this
          sentence: `portfolioPotential` is summed from units and rooms that
          arrive in that bundle, so rendering it early would state CHF 0 for a
          portfolio nobody has counted. */}
      {portfolioLoading ? (
        <p className="mt-3 text-[12px] text-muted-foreground">Portfolio-KPIs werden geladen…</p>
      ) : (
      /* Five columns for five figures, two for the two that survive a range.
         Keeping a five-column track under two figures would leave three empty
         cells reading as missing data — the figures are not missing, they are
         not applicable to this window. */
      <dl
        className={`mt-3 grid grid-cols-2 gap-x-3 gap-y-3 ${
          isRange ? "sm:grid-cols-2" : "sm:grid-cols-3 lg:grid-cols-5"
        }`}
      >
        {figures.map((figure) => (
          <div key={figure.key} data-testid={`performance-secondary-${figure.key}`}>
            <dt className="text-[10px] uppercase tracking-[0.4px] text-muted-foreground">
              {figure.label}
            </dt>
            <dd className="m-0 mt-0.5 text-[16px] font-medium tabular-nums text-card-foreground">
              {figure.value}
            </dd>
            {/* A current-state figure standing in a list under a period heading
                has to say so, or it reads as a period aggregate. */}
            {figure.hint ? (
              <p className="m-0 mt-0.5 text-[10px] leading-snug text-muted-foreground">
                {figure.hint}
              </p>
            ) : null}
          </div>
        ))}
      </dl>
      )}
    </section>
  );
}
